import React, { useState } from 'react';
import {
  Card,
  Input,
  Button,
  Typography,
  Divider,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { SourceEpisode, Hook } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface HookEditorInlineProps {
  episodeId: string;
  existingHook?: Hook;
  onSuccess: (episode: SourceEpisode) => void;
  onCancel: () => void;
}

const HookEditorInline: React.FC<HookEditorInlineProps> = ({
  episodeId,
  existingHook,
  onSuccess,
  onCancel,
}) => {
  const [concept, setConcept] = useState(existingHook?.concept || '');
  const [panels, setPanels] = useState<Array<{ tempId: string; id?: string; caption: string; imageUrl?: string | null }>>(
    existingHook
      ? existingHook.panels.map((p) => ({
          tempId: Math.random().toString(36).substring(7),
          id: p.id,
          caption: p.caption,
          imageUrl: p.imageUrl,
        }))
      : [{ tempId: Math.random().toString(36).substring(7), caption: '' }]
  );
  const [loading, setLoading] = useState(false);

  const isEditMode = !!existingHook;

  const addPanel = () => {
    setPanels([...panels, { tempId: Math.random().toString(36).substring(7), caption: '' }]);
  };

  const removePanel = (tempId: string) => {
    if (panels.length > 1) {
      setPanels(panels.filter((p) => p.tempId !== tempId));
    }
  };

  const updatePanelCaption = (tempId: string, caption: string) => {
    setPanels(
      panels.map((p) => (p.tempId === tempId ? { ...p, caption } : p))
    );
  };

  const handleSave = async () => {
    if (!concept.trim()) {
      message.error('Please enter hook concept');
      return;
    }

    if (panels.some((p) => !p.caption.trim())) {
      message.error('All panels must have a caption');
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        const result = await sourceEpisodesApi.updateHook(episodeId, existingHook!.id, {
          concept,
          panels: panels.map((p) => ({ id: p.id, caption: p.caption })),
        });
        message.success('Hook updated successfully');
        onSuccess(result.data);
      } else {
        const result = await sourceEpisodesApi.createHook(episodeId, {
          concept,
          panels: panels.map((p) => ({ caption: p.caption })),
        });
        message.success('Hook created successfully');
        onSuccess(result.data);
      }
    } catch (error: any) {
      console.error('Failed to save hook:', error);
      message.error(error.response?.data?.error || 'Failed to save hook');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>{isEditMode ? 'Edit Hook' : 'New Hook'}</Title>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
            size="small"
          />
        </div>
      }
      style={{ marginBottom: 16, border: '1px solid #1890ff', borderRadius: 8 }}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4 }}>
          <Text strong>Hook Concept</Text>
        </div>
        <TextArea
          value={concept}
          onChange={(e) => setConcept(e.target.value)}
          placeholder="Enter hook concept (e.g., A mysterious stranger enters the tavern...)"
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong>Panels ({panels.length})</Text>
          <Button
            type="dashed"
            onClick={addPanel}
            icon={<PlusOutlined />}
            size="small"
          >
            Add Panel
          </Button>
        </div>

        {panels.map((panel, index) => (
          <div key={panel.tempId} style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            {panel.imageUrl && (
              <div style={{ flexShrink: 0, width: 48, height: 48, borderRadius: 4, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
                <img src={panel.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <Input
                value={panel.caption}
                onChange={(e) => updatePanelCaption(panel.tempId, e.target.value)}
                placeholder={`Panel ${index + 1} caption...`}
                prefix={<Text type="secondary" style={{ marginRight: 4 }}>#{index + 1}</Text>}
              />
            </div>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removePanel(panel.tempId)}
              disabled={panels.length <= 1}
            />
          </div>
        ))}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={loading}
        block
      >
        {isEditMode ? 'Update Hook' : 'Save Hook'}
      </Button>
    </Card>
  );
};

export default HookEditorInline;
